import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextProps {
  theme: Theme;
  toggleTheme: () => void;
  isDark: boolean;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({
  children,
  defaultTheme = 'light', // Default to light theme
  storageKey = 'marune-theme',
}) => {
  // Initialize theme from localStorage or system preference
  const [theme, setTheme] = useState<Theme>(() => {
    // Check if theme was previously stored
    const storedTheme = localStorage.getItem(storageKey);
    if (storedTheme === 'light' || storedTheme === 'dark') {
      return storedTheme as Theme;
    }

    // Check for system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }

    // Otherwise use default theme
    return defaultTheme;
  });

  // Update localStorage when theme changes
  useEffect(() => {
    localStorage.setItem(storageKey, theme);
    
    // Update document class for CSS selectors
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme, storageKey]);

  // Listen for system preference changes
  useEffect(() => {
    // Only add listener if user preference isn't already set
    if (!localStorage.getItem(storageKey)) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = () => {
        // Only update if user hasn't manually set a preference
        if (!localStorage.getItem(storageKey)) {
          setTheme(mediaQuery.matches ? 'dark' : 'light');
        }
      };
      
      // Modern browsers
      if (mediaQuery.addEventListener) {
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
      } 
      // Safari < 14
      else if ('addListener' in mediaQuery) {
        // @ts-ignore - Safari < 14 support
        mediaQuery.addListener(handleChange);
        return () => {
          // @ts-ignore - Safari < 14 support
          mediaQuery.removeListener(handleChange);
        };
      }
    }
    // Empty return for when we don't add a listener
    return () => {};
  }, [storageKey]);

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  const isDark = theme === 'dark';

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, isDark }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextProps => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  
  return context;
};

export default ThemeProvider;